import { useState } from 'react';

import { Edit as EditIcon } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    styled,
    Typography,
    useTheme,
} from '@mui/material';
import { toSentenceCase } from '@npi/buddy-utils';
import { useFormik } from 'formik';

import { MetaTags, useMutation } from '@redwoodjs/web';

import { CustomInput } from 'src/components/CustomInput';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER } from 'src/constants';
import { UPDATE_USER_ROLE_MUTATION } from 'src/graphql/mutations/userRoles.mutation';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { IEditUserRoleObject } from 'src/types/edit-user-role';

import UserRoleDetails from './UserRoleDetails';

const EditUserRolePage = ({ userRoleObject, closeEditPage, refetch }: IEditUserRoleObject) => {
    const [editUserRoleDetails, setEditUserRoleDetails] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // clean permissions data
    const removeIdAndTypename = (obj) => {
        const { id, __typename, ...rest } = obj;
        return rest;
    };

    const cleanPermissions = removeIdAndTypename(userRoleObject.permissions[0]);

    const [permissions, setPermissions] = useState(cleanPermissions);

    const theme = useTheme();

    // update user role mutation
    const [updateUserRole] = useMutation(UPDATE_USER_ROLE_MUTATION, {
        onCompleted: () => {
            showSuccessToast('User Role Updated Successfully');
            refetch();
            closeEditPage();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Updating the User Role');
        },
    });

    const closeForms = () => {
        if (editUserRoleDetails) {
            setEditUserRoleDetails(false);
        }
    };

    const toggleEditing = () => {
        setEditUserRoleDetails((prev) => {
            return !prev;
        });
    };

    const UserRoleDetailsForm = styled('form')({
        width: '100%',
    });

    const handlePermissionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPermissions({
            ...permissions,
            [event.target.name]: event.target.checked,
        });
    };

    const formik = useFormik({
        initialValues: {
            ...userRoleObject,
        },
        onSubmit: () => {
            setIsModalOpen(true);
        },
    });

    const {
        values: { name, hierarchyLevel },
        handleChange,
        errors: { name: nameError },
        touched: { name: nameTouched },
        handleSubmit,
        isValid,
    } = formik;

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditUserRoleDetails(false);
    };

    const handleSave = () => {
        updateUserRole({
            variables: {
                id: userRoleObject.id,
                input: {
                    name,
                    hierarchyLevel: userRoleObject.hierarchyLevel
                        .toUpperCase()
                        .split(' ')
                        .join('_'),
                    permissions: {
                        ...permissions,
                        id: userRoleObject.permissions[0].id,
                    },
                },
            },
        });

        setIsModalOpen(false);
    };

    return (
        <PageContent
            heading="User Role"
            subHeading="View, Edit or Remove this User Role"
            callToActions={
                <>
                    <IconButton sx={{ color: 'black' }} onClick={toggleEditing}>
                        <EditIcon />
                    </IconButton>
                </>
            }
        >
            <MetaTags title="EditUser page" description="EditUser page" />
            <UserRoleDetailsForm onSubmit={handleSubmit} noValidate autoComplete="off">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                ...DEFAULT_BORDER,
                                height: 'fit-content',
                                backgroundColor: '#FFFFFF',
                                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                padding: '32px 24px',
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '24px',
                                gap: '24px',
                            }}
                            container
                        >
                            {editUserRoleDetails ? (
                                <Grid item xs={12} spacing={2} container>
                                    {/* Role */}
                                    <Grid item xs={12} lg={6}>
                                        <CustomInput
                                            name="name"
                                            label="User Role Name"
                                            placeholder="Enter User Role"
                                            onChange={handleChange}
                                            value={name}
                                            error={nameError}
                                        />
                                    </Grid>

                                    {/* Hierarchy Level */}
                                    <Grid
                                        item
                                        lg={6}
                                        xs={12}
                                        display="flex"
                                        flexDirection="column"
                                        gap="24px"
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                            }}
                                            color={theme.palette.dark.main}
                                        >
                                            Hierarchy Level
                                        </Typography>

                                        <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>
                                            {userRoleObject.hierarchyLevel}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid
                                    item
                                    xs={12}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    {/* Role */}
                                    <Grid lg={6} display="flex" flexDirection="column" gap="24px">
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                            }}
                                            color={theme.palette.dark.main}
                                        >
                                            User Role Name
                                        </Typography>

                                        <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>
                                            {toSentenceCase(userRoleObject.name)}
                                        </Typography>
                                    </Grid>

                                    {/* Hierarchy Level */}
                                    <Grid lg={6} display="flex" flexDirection="column" gap="24px">
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                            }}
                                            color={theme.palette.dark.main}
                                        >
                                            Hierarchy Level
                                        </Typography>

                                        <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>
                                            {userRoleObject.hierarchyLevel}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {/* User Role Features */}
                            <UserRoleDetails
                                permissions={permissions}
                                handlePermissionsChange={handlePermissionsChange}
                                editUserRoleDetails={editUserRoleDetails}
                            />
                        </Grid>
                    </Grid>

                    {/* Submit Buttons */}
                    {editUserRoleDetails && (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '8px',
                            }}
                        >
                            <Button
                                variant="outlined-black"
                                sx={{ width: '146px' }}
                                onClick={closeForms}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="main"
                                sx={{ width: '146px' }}
                                type="submit"
                                disabled={!isValid}
                            >
                                Save
                            </Button>
                        </Grid>
                    )}
                </Box>
            </UserRoleDetailsForm>

            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="delete-user-dialog-title"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseModal}
                        handleAction={handleSave}
                        header="Edit User Role"
                        message="Are you sure you want to save these changes?"
                        buttonText="Save"
                        isEdit
                    />
                </DialogContent>
            </Dialog>
        </PageContent>
    );
};

export default EditUserRolePage;
